import { useCallback, useEffect, useRef, useState } from 'react';
import { resourceCache, useAsyncResource } from 'use-async-resource';
import {
    useGetClients, useGetNewRequestBranchesByClient, useGetNewRequestClientDevices,
    useGetAllBranchMaterials, useGetBranchPaymentInfo,
} from './clients-hooks';
import { useGetAllShortDevicePassports } from './warehouses-hooks';

const useDataById = <T,>(fetchData: (id: number) => Promise<T>, id?: number) => {
    const [cache, setCache] = useState<Record<number, T>>({});
    const [failed, setFailed] = useState<Record<number, boolean>>({});
    const requests = useRef<Record<number, number>>({});
    const mounted = useRef(true);
    useEffect(() => {
        mounted.current = true;
        return () => { mounted.current = false; };
    }, []);
    const reload = useCallback((key: number) => {
        const request = (requests.current[key] ?? 0) + 1;
        requests.current[key] = request;
        const isCurrent = () => mounted.current && requests.current[key] === request;
        return fetchData(key).then(data => {
            if (!isCurrent()) return;
            setCache(current => ({ ...current, [key]: data }));
            setFailed(current => (current[key] ? { ...current, [key]: false } : current));
        }).catch(error => {
            console.error('Failed to load request data', error);
            if (isCurrent()) setFailed(current => ({ ...current, [key]: true }));
        });
    }, [fetchData]);
    return [id ? cache[id] : undefined, reload, Boolean(id && failed[id])] as const;
};

export const useRequestResources = (clientId?: number, branchId?: number) => {
    const [readClients, getClients] = useAsyncResource(useGetClients);
    const [clientBranches, getBranches, branchesFailed] = useDataById(useGetNewRequestBranchesByClient, clientId);
    const [devices, getDevices, devicesFailed] = useDataById(useGetNewRequestClientDevices, branchId);
    const [readDevicePassports] = useAsyncResource(useGetAllShortDevicePassports, false);
    const [materials, getMaterials, materialsFailed] = useDataById(useGetAllBranchMaterials, branchId);
    const [branchPaymentInfo, getBranchPaymentInfo, paymentInfoFailed] =
        useDataById(useGetBranchPaymentInfo, branchId);
    const loaders = useRef({ getBranches, getDevices, getMaterials, getBranchPaymentInfo });
    loaders.current = { getBranches, getDevices, getMaterials, getBranchPaymentInfo };
    const loadBranches = useCallback(() => {
        if (clientId) loaders.current.getBranches(clientId);
    }, [clientId]);
    const loadBranchData = useCallback(() => {
        if (!branchId) return;
        resourceCache(useGetAllBranchMaterials).clear();
        resourceCache(useGetNewRequestClientDevices).clear();
        resourceCache(useGetBranchPaymentInfo).clear();
        loaders.current.getDevices(branchId);
        loaders.current.getMaterials(branchId);
        loaders.current.getBranchPaymentInfo(branchId);
    }, [branchId]);
    useEffect(loadBranches, [loadBranches]);
    useEffect(loadBranchData, [loadBranchData]);
    const retryResources = useCallback(() => {
        loadBranches();
        loadBranchData();
    }, [loadBranches, loadBranchData]);
    return {
        clients: readClients(), getClients, getBranches,
        clientBranches, devices, getDevices, materials, getMaterials,
        branchPaymentInfo, getBranchPaymentInfo,
        devicesPassports: readDevicePassports(),
        resourcesLoadFailed: branchesFailed || devicesFailed || materialsFailed || paymentInfoFailed,
        retryResources,
    };
};
